import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const irisRevealSkill: CodeoSkill = {
    name: SKILL_NAMES.irisReveal,
    kind: 'codeo',
    description:
        'Open a circle out from a chosen point to swap one state of the frame for another — the ' +
        'iris, for a before/after, a zoom into a detail, or revealing what was behind the first state.',
    documentUsesSkill: check(
        (script) => /clipPath|clip-path/.test(script) && /circle\(/.test(script),
    ),
    instructions: `# Iris Reveal

A circle opens from a point on the frame and the state inside it is different from the state outside. It grows until it has replaced the whole frame. Because the two states coexist during the opening, the viewer reads the second as *replacing* the first rather than simply following it.

Anchor the origin on something meaningful — the element the second state is about — and the iris reads as a consequence of that element rather than as a wipe.

## Markup

Both states are full-frame and stacked. The second is clipped; the first needs no treatment at all.

\`\`\`html
<div id="iris-stage" style="position: absolute; inset: 0; overflow: hidden;">
  <div id="state-before" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: var(--l5-color-1, #101820); font-family: var(--l5-font-0), sans-serif; font-size: 120px; font-weight: 800; color: var(--l5-color-0, #ffffff);">Manual</div>
  <div id="state-after" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: var(--l5-color-2, #38bdf8); font-family: var(--l5-font-0), sans-serif; font-size: 120px; font-weight: 800; color: var(--l5-color-1, #101820);">Automatic</div>
</div>
\`\`\`

## Timeline code

Tween the \`clipPath\` string. The radius must reach the far corner of the frame, or the iris stops short and leaves a ring of the old state around the edges.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const ORIGIN_X = 62; // percent
const ORIGIN_Y = 44;
// Distance from the origin to the furthest corner, in percent of the frame diagonal.
const REACH = 92;

gsap.set('#state-after', { clipPath: \`circle(0% at \${ORIGIN_X}% \${ORIGIN_Y}%)\` });

tl.to('#state-after', {
    clipPath: \`circle(\${REACH}% at \${ORIGIN_X}% \${ORIGIN_Y}%)\`,
    duration: 0.9,
    ease: 'power2.inOut',
}, 1.1);

// The first state dims as the iris takes over, so the swap reads as a change of register.
tl.to('#state-before', { filter: 'saturate(0.5) brightness(0.8)', duration: 0.6, ease: 'power2.out' }, 1.1);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Origin**: a corner origin reads as a sweep; a centre origin reads as an announcement; an origin on a specific element reads as *that element* causing the change. The last is nearly always the best one.
- **Closing iris**: reverse it (\`REACH\` → 0) to collapse onto a final logo or number — the classic close.
- **Rim**: an SVG circle tracking the same radius with a 6px accent stroke gives the iris an edge and hides any anti-aliasing seam.
- **Ease**: \`power2.inOut\` is confident. \`power4.out\` opens explosively and settles, which suits a reveal that is meant to feel like a discovery.
- **Squircle**: \`inset(... round Npx)\` growing from a small rounded rect instead of a circle reads as a panel expanding rather than a lens opening.
- **Register shift**: desaturating the before-state (above) is what makes the after-state land in full colour. Drop it when both states are already distinct.

## Pitfalls

- \`REACH\` must clear the furthest corner from the origin. For an off-centre origin that can be well over 100% — compute it rather than assuming.
- Tween the whole \`clipPath\` string in both from and to states, including the \`at X% Y%\` part; changing the origin mid-tween makes the circle slide as it grows.
- The clipped element must be full-frame and absolutely positioned, or the percentage radius resolves against the wrong box.
- Don't move either state during the iris. The iris *is* the motion; sliding the contents as well produces two competing reads.
- One iris per graphic. It is a scene-level punctuation mark.
- If the states carry text, both must be legible at the moment they coexist — an iris passing over a headline mid-word is unreadable.
- A single iris and then a static hold is dead air; give the after-state something to do once it owns the frame — see \`${SKILL_NAMES.ambientFloatLoop}\`.`,
};
